import React, { useEffect, useState } from "react";
import { CARTOMANCER_LOGO } from "../branding";

// Evita il doppio splash causato da React StrictMode in development.
let _splashShownThisSession = false;

const SPLASH_STORAGE_KEY = "cartomancer_splash_shown";

const SplashScreen = ({
  duration = 1800,
  onDone,
  forceShow = false,
}) => {
  const [phase, setPhase] = useState("enter");
  const [visible] = useState(() => {
    if (typeof window === "undefined") return false;

    // forceShow ignora completamente la sessione.
    if (forceShow) return true;

    // Protezione StrictMode.
    if (_splashShownThisSession) return false;

    try {
      const alreadyShown = sessionStorage.getItem(SPLASH_STORAGE_KEY);

      if (alreadyShown === "1") {
        _splashShownThisSession = true;
        return false;
      }
    } catch (_error) {
      // Se sessionStorage non è disponibile,
      // mostriamo comunque lo splash.
    }

    return true;
  });

  useEffect(() => {
    if (!visible) {
      onDone?.();
      return undefined;
    }

    /*
     * Timeline:
     *
     * 0ms       → inizio
     * 450ms     → logo completamente visibile
     * ~1200ms   → permanenza
     * ~1500ms   → uscita
     * ~1800ms   → fine
     */

    const fadeInMs = 450;
    const fadeOutMs = 450;

    const holdMs = Math.max(
      300,
      duration - fadeInMs - fadeOutMs
    );

    const t1 = setTimeout(() => {
      setPhase("hold");
    }, fadeInMs);

    const t2 = setTimeout(() => {
      setPhase("exit");
    }, fadeInMs + holdMs);

    const t3 = setTimeout(() => {
      setPhase("done");

      if (!forceShow) {
        _splashShownThisSession = true;

        try {
          sessionStorage.setItem(
            SPLASH_STORAGE_KEY,
            "1"
          );
        } catch (_error) {
          // Ignora errori di storage.
        }
      }

      onDone?.();
    }, fadeInMs + holdMs + fadeOutMs);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [visible, duration, onDone, forceShow]);

  if (!visible || phase === "done") {
    return null;
  }

  const isEntering = phase === "enter";
  const isExiting = phase === "exit";

  const opacity = isEntering || isExiting ? 0 : 1;

  const logoScale = isEntering
    ? 0.82
    : isExiting
      ? 1.06
      : 1;

  const logoBlur = isEntering
    ? 10
    : isExiting
      ? 3
      : 0;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      data-testid="cartomancer-splash"
      aria-label="Cartomancer"
      role="status"
      style={{
        opacity,
        transition: isEntering
          ? "opacity 450ms cubic-bezier(0.22, 1, 0.36, 1)"
          : isExiting
            ? "opacity 450ms cubic-bezier(0.4, 0, 1, 1)"
            : "opacity 300ms ease-out",

        background: `
          radial-gradient(
            circle at 50% 50%,
            rgba(212, 175, 55, 0.075) 0%,
            rgba(212, 175, 55, 0.025) 25%,
            transparent 55%
          ),
          radial-gradient(
            circle at 20% 30%,
            rgba(120, 80, 180, 0.08),
            transparent 35%
          ),
          radial-gradient(
            circle at 80% 70%,
            rgba(150, 30, 50, 0.06),
            transparent 35%
          )
        `,

        backdropFilter: "blur(10px)",
      }}
    >
      {/* =====================================================
          BACKGROUND MAGIC
      ====================================================== */}

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* Main magical aura */}
        <div
          className="absolute left-1/2 top-1/2 h-[700px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/[0.06] blur-[100px]"
          style={{
            animation:
              "cartomancer-aura 3.5s ease-in-out infinite",
          }}
        />

        {/* Arcane glow */}
        <div
          className="absolute left-[12%] top-[18%] h-48 w-48 rounded-full bg-arcane/[0.08] blur-[90px]"
          style={{
            animation:
              "cartomancer-float-one 5s ease-in-out infinite",
          }}
        />

        {/* Ruby glow */}
        <div
          className="absolute bottom-[15%] right-[12%] h-44 w-44 rounded-full bg-ruby/[0.07] blur-[90px]"
          style={{
            animation:
              "cartomancer-float-two 6s ease-in-out infinite",
          }}
        />

        {/* =================================================
            RUNE CIRCLE
        ================================================== */}

        <div
          className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-gold/[0.08]"
          style={{
            animation:
              "cartomancer-rotate 30s linear infinite",
          }}
        >
          <div className="absolute -left-1 top-1/2 h-2 w-2 rounded-full bg-gold/40 shadow-[0_0_12px_rgba(212,175,55,0.6)]" />
          <div className="absolute -right-1 top-1/2 h-2 w-2 rounded-full bg-gold/40 shadow-[0_0_12px_rgba(212,175,55,0.6)]" />
          <div className="absolute left-1/2 -top-1 h-2 w-2 rounded-full bg-gold/30" />
          <div className="absolute left-1/2 -bottom-1 h-2 w-2 rounded-full bg-gold/30" />
        </div>

        <div
          className="absolute left-1/2 top-1/2 h-[430px] w-[430px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-gold/[0.045]"
          style={{
            animation:
              "cartomancer-rotate-reverse 24s linear infinite",
          }}
        />

        {/* =================================================
            FLOATING PARTICLES
        ================================================== */}

        <span className="splash-particle particle-1" />
        <span className="splash-particle particle-2" />
        <span className="splash-particle particle-3" />
        <span className="splash-particle particle-4" />
        <span className="splash-particle particle-5" />
        <span className="splash-particle particle-6" />
        <span className="splash-particle particle-7" />
        <span className="splash-particle particle-8" />

        {/* Tiny stars */}
        <span className="splash-star star-1">✦</span>
        <span className="splash-star star-2">✧</span>
        <span className="splash-star star-3">✦</span>
        <span className="splash-star star-4">✧</span>
      </div>

      {/* =====================================================
          LOGO
      ====================================================== */}

      <div
        className="relative z-10 flex flex-col items-center"
        style={{
          transform: `scale(${logoScale})`,
          filter: `blur(${logoBlur}px)`,

          transition: isEntering
            ? "transform 700ms cubic-bezier(0.16, 1, 0.3, 1), filter 700ms ease-out"
            : isExiting
              ? "transform 500ms cubic-bezier(0.4, 0, 1, 1), filter 500ms ease-out"
              : "transform 300ms ease-out, filter 300ms ease-out",
        }}
      >
        {/* Outer magical halo */}
        <div
          className="absolute left-1/2 top-1/2 -z-10 h-[380px] w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/[0.04] blur-[60px]"
          style={{
            animation:
              "cartomancer-logo-glow 2.8s ease-in-out infinite",
          }}
        />

        {/* Logo */}
        <img
          src={CARTOMANCER_LOGO}
          alt="Cartomancer"
          className="
            relative
            max-h-[52vh]
            max-w-[82vw]
            object-contain
          "
          style={{
            animation:
              "cartomancer-logo-breathe 3s ease-in-out infinite",

            filter: `
              drop-shadow(0 0 12px rgba(217,178,52,0.25))
              drop-shadow(0 12px 40px rgba(217,178,52,0.38))
              drop-shadow(0 0 70px rgba(217,178,52,0.12))
            `,
          }}
          data-testid="cartomancer-splash-logo"
        />

        {/* Divider */}
        <div
          className="
            mt-7
            flex
            w-full
            items-center
            justify-center
            gap-4
          "
        >
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold/30" />

          <div
            className="
              h-1.5
              w-1.5
              rotate-45
              bg-gold/60
              shadow-[0_0_10px_rgba(212,175,55,0.45)]
            "
          />

          <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold/30" />
        </div>

        {/* Subtitle */}
        <p
          className="
            mt-5
            font-heading
            text-[10px]
            font-medium
            uppercase
            tracking-[0.55em]
            text-gold/65
          "
          style={{
            animation:
              "cartomancer-text-fade 1.2s ease-out 300ms both",
          }}
        >
          Preparando l'avventura
        </p>

        {/* Loading rune */}
        <div
          className="
            mt-5
            flex
            items-center
            gap-2
            text-gold/30
          "
        >
          <span className="h-px w-5 bg-gold/20" />

          <span
            className="text-[9px]"
            style={{
              animation:
                "cartomancer-rune-pulse 1.4s ease-in-out infinite",
            }}
          >
            ✦
          </span>

          <span className="h-px w-5 bg-gold/20" />
        </div>
      </div>

      {/* =====================================================
          ANIMATIONS
      ====================================================== */}

      <style>{`
        @keyframes cartomancer-aura {
          0%, 100% {
            transform:
              translate(-50%, -50%)
              scale(0.88);
            opacity: 0.45;
          }

          50% {
            transform:
              translate(-50%, -50%)
              scale(1.08);
            opacity: 0.8;
          }
        }

        @keyframes cartomancer-logo-glow {
          0%, 100% {
            transform:
              translate(-50%, -50%)
              scale(0.88);
            opacity: 0.35;
          }

          50% {
            transform:
              translate(-50%, -50%)
              scale(1.08);
            opacity: 0.75;
          }
        }

        @keyframes cartomancer-logo-breathe {
          0%, 100% {
            transform: scale(1);
          }

          50% {
            transform: scale(1.015);
          }
        }

        @keyframes cartomancer-rotate {
          from {
            transform:
              translate(-50%, -50%)
              rotate(0deg);
          }

          to {
            transform:
              translate(-50%, -50%)
              rotate(360deg);
          }
        }

        @keyframes cartomancer-rotate-reverse {
          from {
            transform:
              translate(-50%, -50%)
              rotate(360deg);
          }

          to {
            transform:
              translate(-50%, -50%)
              rotate(0deg);
          }
        }

        @keyframes cartomancer-float-one {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }

          50% {
            transform: translate(20px, -25px) scale(1.08);
          }
        }

        @keyframes cartomancer-float-two {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }

          50% {
            transform: translate(-25px, 20px) scale(1.1);
          }
        }

        @keyframes cartomancer-text-fade {
          from {
            opacity: 0;
            transform: translateY(5px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes cartomancer-rune-pulse {
          0%, 100% {
            opacity: 0.25;
            transform: scale(0.8);
          }

          50% {
            opacity: 1;
            transform: scale(1.2);
          }
        }

        .splash-particle {
          position: absolute;
          display: block;
          width: 3px;
          height: 3px;
          border-radius: 9999px;
          background: rgba(217, 178, 52, 0.45);
          box-shadow:
            0 0 8px rgba(217, 178, 52, 0.5),
            0 0 18px rgba(217, 178, 52, 0.2);
          animation:
            cartomancer-particle 3.5s ease-in-out infinite;
        }

        .particle-1 {
          left: 18%;
          top: 28%;
          animation-delay: 0s;
        }

        .particle-2 {
          left: 75%;
          top: 24%;
          animation-delay: 0.7s;
        }

        .particle-3 {
          left: 24%;
          top: 68%;
          animation-delay: 1.2s;
        }

        .particle-4 {
          left: 78%;
          top: 66%;
          animation-delay: 1.8s;
        }

        .particle-5 {
          left: 35%;
          top: 18%;
          animation-delay: 2.2s;
        }

        .particle-6 {
          left: 65%;
          top: 78%;
          animation-delay: 2.7s;
        }

        .particle-7 {
          left: 12%;
          top: 55%;
          animation-delay: 1.5s;
        }

        .particle-8 {
          left: 88%;
          top: 48%;
          animation-delay: 0.4s;
        }

        @keyframes cartomancer-particle {
          0%, 100% {
            opacity: 0.15;
            transform: translateY(10px) scale(0.7);
          }

          50% {
            opacity: 0.85;
            transform: translateY(-20px) scale(1.25);
          }
        }

        .splash-star {
          position: absolute;
          color: rgba(217, 178, 52, 0.3);
          font-size: 12px;
          animation:
            cartomancer-star 2.8s ease-in-out infinite;
        }

        .star-1 {
          left: 22%;
          top: 40%;
          animation-delay: 0.2s;
        }

        .star-2 {
          left: 82%;
          top: 35%;
          animation-delay: 1s;
        }

        .star-3 {
          left: 30%;
          top: 78%;
          animation-delay: 1.7s;
        }

        .star-4 {
          left: 70%;
          top: 16%;
          animation-delay: 2.1s;
        }

        @keyframes cartomancer-star {
          0%, 100% {
            opacity: 0.15;
            transform: rotate(0deg) scale(0.7);
          }

          50% {
            opacity: 0.7;
            transform: rotate(45deg) scale(1.15);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .splash-particle,
          .splash-star,
          .splash-particle,
          .splash-star {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SplashScreen;